import React, { CSSProperties, useMemo, useState } from "react";
import { Header, flexRender } from "@tanstack/react-table";
import TableFilter from "../TableFilter/TableFilter";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import TheadCellActions from "./TheadCellActions";
import classNames from "classnames";
import Tooltip from "../../Tooltip/Tooltip";

interface TheadCellProps {
  header: Header<unknown, unknown>;
  dataTestId?: string;
  enableTableViewEdit?: boolean;
  numOfRows?: number;
  rowHeight?: number | string;
  lastPinnedColumn?: number;
  hasComplexSorting?: boolean;
  sortOnHeaderClick?: boolean;
}

const TheadCell: React.FC<TheadCellProps> = ({
  header,
  dataTestId,
  enableTableViewEdit,
  numOfRows,
  rowHeight,
  lastPinnedColumn,
  hasComplexSorting = false,
  sortOnHeaderClick = false,
}) => {
  const [isActionsMenuOpen, setIsActionsMenuOpen] = useState(false);
  const headerDisplayValue = header.column.columnDef.header;

  const toggleSorting =
    sortOnHeaderClick && !hasComplexSorting && header.column.getCanSort()
      ? header.column.getToggleSortingHandler()
      : undefined;

  // hideOnEdit is a custom columnDef meta property not present on the base ColumnDef type
  const columnDef = header.column.columnDef as { hideOnEdit?: boolean };

  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useSortable({
      id: header.column.id,
    });

  const pinnedStyles = useMemo<CSSProperties>(() => {
    const { column } = header;
    const hidePin = (column?.columnDef as { hideOnEdit?: boolean })?.hideOnEdit;
    const isPinned = column.getIsPinned();
    const isLastLeftPinnedColumn =
      !hidePin && !!isPinned && column.getPinnedIndex() === lastPinnedColumn;

    return {
      boxShadow: isLastLeftPinnedColumn
        ? "inset -1px 0px 0px 0px var(--table-border-color)"
        : "",
      left: isPinned ? `${column.getStart("left")}px` : "unset",
      zIndex: isPinned || isDragging ? 100 : 0,
    };
  }, [header, lastPinnedColumn, isDragging, header?.column?.getStart("left")]);

  const classNameWrapper = classNames("th", {
    "is-sortable": header.column.getCanSort(),
    "is-sorted": header.column.getIsSorted(),
    "is-complex-sorted": hasComplexSorting && header.column.getIsSorted(),
    "is-draggable":
      enableTableViewEdit &&
      !columnDef?.hideOnEdit &&
      !header.column.getIsPinned(),
    "is-dragging": isDragging,
    "static-cell": !!columnDef?.hideOnEdit,
    "actions-menu-open": isActionsMenuOpen,
  });

  return (
    <th
      {...{
        style: {
          width: header.getSize(),
          transform: CSS.Translate.toString(transform),
          ...pinnedStyles,
        },
        className: classNameWrapper,
      }}
      key={header.id}
      data-testid={`${dataTestId}-header-row-${header.id}`}
      ref={
        enableTableViewEdit && !columnDef?.hideOnEdit ? setNodeRef : null
      }
    >
      {!header.isPlaceholder && (
        <div className={"header-title-container-wrap"}>
          <div
            {...{
              className: `header-title-container ${
                enableTableViewEdit ? "has-actions" : ""
              }`,
            }}
          >
            <div className={"dragging-handle"} {...attributes} {...listeners} />
            {enableTableViewEdit && !columnDef?.hideOnEdit && (
              <>
                <div
                  className={`${enableTableViewEdit ? "resizer" : ""} ${
                    enableTableViewEdit && header.column.getIsResizing()
                      ? "is-resizing"
                      : ""
                  }`}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    header.getResizeHandler()(e);
                  }}
                  onTouchStart={(e) => {
                    e.stopPropagation();
                    header.getResizeHandler()(e);
                  }}
                  style={{
                    height: `calc(100% + ${rowHeight}px * ${numOfRows})`,
                  }}
                />
              </>
            )}
            <div className="header-title" onClick={toggleSorting}>
              <Tooltip
                content={
                  !enableTableViewEdit
                    ? typeof headerDisplayValue === "string"
                      ? headerDisplayValue
                      : ""
                    : ""
                }
              >
                <span>
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                </span>
              </Tooltip>
              {((!hasComplexSorting && header.column.getCanSort()) ||
                !!header.column.getIsSorted()) && (
                <i
                  onClick={(e) => {
                    // The arrow sits inside .header-title, which carries the same handler when
                    // sortOnHeaderClick is on -- without this the click would toggle twice.
                    e.stopPropagation();
                    if (!hasComplexSorting) {
                      header.column.getToggleSortingHandler()?.(e);
                    }
                  }}
                  className={`seem-icon ${
                    header.column.getIsSorted() === "desc"
                      ? "seem-icon-arrow-down"
                      : "seem-icon-arrow-up"
                  } sort-icon`}
                  data-testid={`sort-${
                    header.column.getIsSorted() === "desc" ? "down" : "up"
                  }`}
                />
              )}
            </div>
            {enableTableViewEdit && !columnDef?.hideOnEdit && (
              <TheadCellActions
                column={header?.column}
                setIsActive={setIsActionsMenuOpen}
              />
            )}
          </div>
          {header.column.getCanFilter() && (
            <div className={"table-filter-wrap"}>
              <TableFilter column={header.column} />
            </div>
          )}
        </div>
      )}
    </th>
  );
};

export default TheadCell;
